/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { getAvailableColumns, getVisibleColumns } from '@/stores/helpers/voc-items.helper';
import { PerSubscriptionUserStorage } from '@utils/storage';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';
import { useVocItemsJiraStore } from '@/stores/voc-items-jira.store';

interface SortConfig {
    prop: string;
    order: string;
}

interface TableState {
    visibleColumns: string[];
    columnWidths: Record<string, number> | null;
    type: string;
    sort: SortConfig;
    defaultSort: SortConfig;
}

export function getInitialState (): TableState {
    return {
        visibleColumns: [],
        columnWidths: null,
        type: '',
        sort: {
            prop: 'lastUpdatedAt',
            order: 'descending',
        },
        defaultSort: {
            prop: 'lastUpdatedAt',
            order: 'descending',
        },
    };
}

export const useVocItemsTableStore = defineStore('vocItems/table', {
    state: getInitialState,
    getters: {
        availableColumns (state) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const { settings } = useSubscriptionStore() || {};
            const revenueLabel = !get(settings, 'revenue.label') ? 'Revenue' : get(settings, 'revenue.label', 'Revenue');

            return getAvailableColumns({
                type: state.type,
                hasSegmentFlag,
                isMultiApp: useAppsStore().isMultiApp,
                revenueLabel,
                getFeatureFlag: useSubscriptionStore().getFeatureFlag,
                hasProductLinePurchased: activeSubHasPurchasedProductLine,
                hasJiraIntegration: useVocItemsJiraStore().shouldShowJiraIntegration,
                jiraBaseUrl: useVocItemsJiraStore().serverInfo?.baseUrl as string,
            });
        },
        aggregationSortArg (state): string {
            const column = this.availableColumns.find((col) => {
                return col.prop === state.sort.prop;
            });

            const descAscOperator = state.sort.order === 'descending' ? '-' : '';
            const sortFieldPropName = column?.sortField || column?.prop;

            if (!sortFieldPropName) return '-lastUpdatedAt';

            const sortArg = descAscOperator + sortFieldPropName;

            return sortArg;
        },
        columns (state): unknown[] {
            const visibleColumns = new Array(state.visibleColumns.length);
            const hiddenColumns: unknown[] = [];

            this.availableColumns.forEach((column: any) => {
                const indexOfColumn = state.visibleColumns.indexOf(column.prop);
                const savedColumnWidth = get(state.columnWidths, `['${column.prop}']`) || column.width;

                if (indexOfColumn !== -1) {
                    visibleColumns[indexOfColumn] = {
                        ...column,
                        visible: true,
                        ...(savedColumnWidth && { width: savedColumnWidth }),
                    };
                } else {
                    hiddenColumns.push({
                        ...column,
                        visible: false,
                        ...(savedColumnWidth && { width: savedColumnWidth }),
                    });
                }
            });

            return [...visibleColumns.filter((column) => !!column), ...hiddenColumns];
        },
        tableConfig (state): Record<string, unknown> {
            return {
                ...(state.sort && { sort: state.sort }),
                ...(state.visibleColumns && state.visibleColumns.length && { columns: state.visibleColumns }),
                ...(state.columnWidths && { columnWidths: state.columnWidths }),
            };
        },
        defaultTableConfig (state): Record<string, unknown> {
            return {
                sort: state.defaultSort,
                columns: getVisibleColumns({ type: state.type, storedColumns: undefined }),
                columnWidths: null,
            };
        },
        emptyTableConfig (state): Record<string, unknown> {
            return {
                sort: state.defaultSort,
                columns: getVisibleColumns({ type: state.type, storedColumns: [] }),
            };
        },
        feedbackEmptyTableConfig (state): Record<string, unknown> {
            return {
                sort: state.defaultSort,
                columns: getVisibleColumns({ type: 'feedback', storedColumns: [] }),
            };
        },
    },
    actions: {
        setVisibleColumns ({ visibleColumns }: { visibleColumns: string[] | null }) {
            this.visibleColumns = visibleColumns || [];
        },
        setColumnWidths ({ columnWidths }: { columnWidths: Record<string, number> | null }) {
            this.columnWidths = columnWidths;
        },
        setSort ({ sort }: { sort: SortConfig }) {
            this.sort = sort;
        },
        setType ({ type }: { type: string }) {
            this.type = type;
        },
        async loadAll ({ type }: { type: string }) {
            this.setType({ type });

            let storedColumns: string[] | null = this.visibleColumns.length && this.type === type ? this.visibleColumns : null;
            if (type === 'idea') {
                const [sortConfig, columnConfig, columnWidthsConfig] = await Promise.all([
                    PerSubscriptionUserStorage.get('vocListSort'),
                    PerSubscriptionUserStorage.get('vocListColumns'),
                    PerSubscriptionUserStorage.get('vocListColumnWidths'),
                ]);
                storedColumns = get(columnConfig, 'value') as string[] | null;

                this.setColumnWidths({ columnWidths: (get(columnWidthsConfig, 'value') as Record<string, number> | null) || null });

                if (get(sortConfig, 'value')) {
                    this.setSort({ sort: get(sortConfig, 'value') as SortConfig });
                }
            }
            const visibleColumns = getVisibleColumns({ type, storedColumns: storedColumns ?? undefined });
            this.setVisibleColumns({ visibleColumns });
        },
        async update ({
            sort,
            columns,
            columnWidths,
            type,
            columnWidthsAppend = false,
        }: {
            sort?: SortConfig | null;
            columns?: string[] | null;
            columnWidths?: Record<string, number> | null;
            type?: string;
            columnWidthsAppend?: boolean;
        }) {
            if (sort !== undefined && sort !== this.sort) {
                if (!sort) sort = this.defaultSort;

                this.setSort({ sort });

                if (type === 'idea') {
                    if (isEqual(sort, this.defaultSort) && (await PerSubscriptionUserStorage.get('vocListSort'))) {
                        PerSubscriptionUserStorage.remove('vocListSort');
                    } else if (!isEqual(sort, await PerSubscriptionUserStorage.get('vocListSort'))) {
                        PerSubscriptionUserStorage.set('vocListSort', sort);
                    }
                }
            }
            if (columns !== undefined && columns !== this.visibleColumns) {
                if (!columns) {
                    columns = getVisibleColumns({ type: this.type, storedColumns: undefined });
                }
                this.setVisibleColumns({ visibleColumns: columns ?? null });

                if (type === 'idea') {
                    if (isEqual(columns, getVisibleColumns({ type: this.type, storedColumns: undefined }))) {
                        PerSubscriptionUserStorage.remove('vocListColumns');
                    } else {
                        PerSubscriptionUserStorage.set('vocListColumns', JSON.stringify(columns));
                    }
                }
            }
            if (columnWidths !== undefined) {
                let newColumnWidths: Record<string, number> | null = null;

                if (columnWidths && columnWidthsAppend) {
                    newColumnWidths = {
                        ...this.columnWidths,
                        ...columnWidths,
                    };
                } else {
                    newColumnWidths = columnWidths;
                }

                if (this.columnWidths !== newColumnWidths) {
                    this.setColumnWidths({ columnWidths: newColumnWidths });
                    if (type === 'idea') {
                        if (newColumnWidths) {
                            PerSubscriptionUserStorage.set('vocListColumnWidths', JSON.stringify(newColumnWidths));
                        } else {
                            PerSubscriptionUserStorage.remove('vocListColumnWidths');
                        }
                    }
                }
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocItemsTableStore, module.hot));
}
